import React, { useState, useEffect, useRef } from 'react';
import { Card, Input, Select, Button, Space, Typography, message } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { DataMapApi } from '@/api/dataMapApi';

const { Option } = Select;

interface OntologySearchPanelProps {
  onSaveViewState?: () => void;
  onRestoreViewState?: () => void;
  onResetView?: () => void;
}

interface OntologyObject {
  id: string;
  name: string;
  description?: string;
}

const OntologySearchPanel: React.FC<OntologySearchPanelProps> = ({
  onSaveViewState,
  onRestoreViewState,
  onResetView
}) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [ontologyObjects, setOntologyObjects] = useState<OntologyObject[]>([]);
  const [filteredObjects, setFilteredObjects] = useState<OntologyObject[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedObject, setSelectedObject] = useState<OntologyObject | null>(null);
  const [loading, setLoading] = useState(false);
  
  const searchInputRef = useRef<InputRef>(null);

  // 从URL中获取ontologyId
  const getInitialOntologyId = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('ontologyId');
  };

  // 加载ontology对象列表
  const loadOntologyObjects = async () => {
    setLoading(true);
    try {
      // 这里需要获取一个有效的ontologyId，暂时使用一个默认值
      // 实际应用中应该从URL或其他来源获取
      const initialOntologyId = getInitialOntologyId() || '1'; // 默认值，实际应根据情况修改
      const response = await DataMapApi.getSettingsMappingObjectsList(initialOntologyId);
      
      if (response.code === 200) {
        setOntologyObjects(response.data);
        setFilteredObjects(response.data);
        
        // 如果URL中有ontologyId，尝试默认选中对应的对象
        if (initialOntologyId) {
          const defaultObject = response.data.find((obj: OntologyObject) => obj.id === initialOntologyId);
          if (defaultObject) {
            setSelectedObject(defaultObject);
            setSearchKeyword(defaultObject.name);
          }
        }
      } else {
        message.error('获取 ontology 对象列表失败');
      }
    } catch (error) {
      console.error('加载 ontology 对象失败:', error);
      message.error('加载 ontology 对象失败');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载数据
  useEffect(() => {
    loadOntologyObjects();
  }, []);

  // 搜索处理
  const handleSearch = () => {
    if (!searchKeyword?.trim()) {
      setFilteredObjects(ontologyObjects);
      // 搜索关键字为空时不显示列表
      setShowSearchResults(false);
    } else {
      const keyword = searchKeyword.toLowerCase()?.trim();
      const filtered = ontologyObjects.filter(obj => {
        return obj.name.toLowerCase().includes(keyword) ||
               (obj.description && obj.description.toLowerCase().includes(keyword));
      });
      // 限制最多展示五个结果
      setFilteredObjects(filtered.slice(0, 5));
      // 搜索关键字不为空时显示列表
      setShowSearchResults(true);
    }
  };

  // 搜索框失焦处理
  const handleSearchBlur = () => {
    // 延迟隐藏，让点击事件能够触发
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200);
  };

  // 选择ontology对象
  const selectOntologyObject = (obj: OntologyObject) => {
    setSelectedObject(obj);
    setSearchKeyword(obj.name);
    setShowSearchResults(false);
    
    // 更新URL中的ontologyId并刷新页面
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('id', obj.id);
    const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
    window.location.href = newUrl;
    
    console.log('选择了 ontology 对象:', obj, newUrl, urlParams);
  };

  // 清除搜索
  const handleClearSearch = () => {
    setSearchKeyword('');
    setSelectedObject(null);
    setFilteredObjects(ontologyObjects);
    setShowSearchResults(false);
  };

  // 监听搜索输入变化
  useEffect(() => {
    handleSearch();
  }, [searchKeyword, ontologyObjects]);

  return (
    <Card
      // title="Ontology 搜索"
      // size="small"
    >
      <div className="space-y-3">
        {/* 搜索输入 */}
        <div className="search-container" style={{ position: 'relative' }}>
          <Input
            ref={searchInputRef}
            placeholder="输入 ontology 对象名称搜索..."
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onFocus={() => setShowSearchResults(true)}
            onBlur={handleSearchBlur}
            suffix={
              <Button type="text" icon={<SearchOutlined />} size="small" />
            }
          />

          {/* 搜索结果下拉列表 */}
          {showSearchResults && filteredObjects.length > 0 && (
            <div
              className="search-results"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {filteredObjects.map(obj => (
                <div
                  key={obj.id}
                  onClick={() => selectOntologyObject(obj)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    {obj.name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {showSearchResults && searchKeyword && filteredObjects.length === 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                padding: '12px',
                textAlign: 'center',
                color: '#999',
              }}
            >
              未找到匹配的 ontology 对象
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        {/* {(searchKeyword || selectedObject) && (
          <div className="flex items-center space-x-2">
            <Button
              size="small"
              onClick={handleClearSearch}
            >
              清除搜索
            </Button>
          </div>
        )} */}

        {/* 当前选中的 ontology 对象信息 */}
        {/* {selectedObject && (
          <div
            style={{
              padding: '8px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '4px',
              fontSize: '12px',
            }}
          >
            当前选中: {selectedObject.name}
            {selectedObject.description && (
              <div style={{ marginTop: '4px', color: '#666' }}>
                {selectedObject.description}
              </div>
            )}
          </div>
        )} */}
      </div>
    </Card>
  );
};

export default OntologySearchPanel;